import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import logo from "../assets/images/coyote-logo.jpg";
import "./bom.css";
import axios from "axios";
import url from "../configs/config";
import { jwtDecode } from "jwt-decode";
import FadeLoaderComponent from "../components/loader-component";
import { roleTypes } from "../data/roleType";
import * as SettingSelector from "../store/setting/selectors";
import { useSelector } from "react-redux";
import html2pdf from "html2pdf.js";

const JobBomView = () => {
  const { id: jobId, bomId } = useParams();
  const navigate = useNavigate();
  const alertRef = useRef(null);
  const printContentRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [bomData, setBomData] = useState({});
  const [bomInfo, setBomInfo] = useState({});
  const [totals, setTotals] = useState({
    totalProducts: 0,
    totalRequired: 0,
    totalAvailable: 0,
    totalToOrder: 0,
  });
  const [decodedToken, setDecodedToken] = useState();
  const token = localStorage.getItem("coyote-token");
  const permissions = useSelector(SettingSelector.set_permissions);
  const hasBomPrintPermission = permissions?.job?.print_bom === true;
  const [jobName, setJobName] = useState("");

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const getBomData = async () => {
    const config = {
      headers: {
        Authorization: `${token}`,
      },
    };

    try {
      const response = await axios.get(
        `${url.backendURL}job/${jobId}/bom/${bomId}`,
        config
      );

      if (response.data.response.data) {
        setBomData(response.data.response.data.bomData);
        setBomInfo(response.data.response.data.bomInfo);
        setTotals(response.data.response.data.totals);
        setJobName(response.data.response.data.bomInfo.jobName || "");
      }
      setLoading(false);
    } catch (err) {
      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getBomData();
      } catch (err) { }
    };
    if (decodedToken) {
      fetchData();
    }
  }, [decodedToken, jobId, bomId]);

  const handleError = (err) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");
      navigate(
        `/${decodedToken?.roles?.some(
          (role) => role.role_type === roleTypes.SUPER_ADMIN
        )
          ? "admin"
          : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      console.error(
        "Error loading BOM:",
        response?.data?.message || err.message
      );
    }
  };

  const handleDownloadPDF = () => {
    if (!printContentRef.current) return;

    const printContent = printContentRef.current.innerHTML;

    const pdfContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${bomInfo.bomNumber || "BOM-Report"}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              color: #000;
              margin: 0;
              padding: 0;
            }
            
            .page-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 2px solid #0056b3;
              padding-bottom: 8px;
              margin-bottom: 10px;
            }
            
            .page-footer {
              text-align: center;
              border-top: 2px solid #3a57e8;
              padding-top: 6px;
              font-size: 7pt;
              font-weight: 600;
              color: #333;
              background: white;
              margin-top: 20px;
            }
            
            .printLogo {
              height: auto;
              width: 70px;
            }
            
            .printTitle {
              text-align: right;
            }
            
            .printMainTitle {
              margin: 0;
              font-size: 13pt;
              color: #3a57e8;
              font-weight: 600;
              letter-spacing: 0.5px;
            }
            
            .printBomNumber {
              margin: 3px 0 0 0;
              font-size: 7.5pt;
            }
            
            .printBold {
              font-weight: bold;
            }
            
            .printContent {
              padding-bottom: 20mm;
            }
            
            .printMetadata {
              border: 1px solid #ddd;
              padding: 8px 10px;
              margin-bottom: 10px;
              background-color: #f5f5f5;
            }

            .printMetadataRow {
              display: flex;
              justify-content: space-between;
              margin-bottom: 4px;
            }

            .printMetadataRow:last-child {
              margin-bottom: 0;
            }

            .printMetadataItem {
              flex: 1;
              font-size: 8pt;
              display: flex;
            }

            .printMetadataLabel {
              font-weight: bold;
              min-width: 120px;
              display: inline-block;
            }
            
            .printHeading {
              margin: 10px 0 8px 0;
              font-size: 11pt;
              font-weight: 600;
            }
            
            .printSystem {
              margin-bottom: 12px;
            }
            
            .printSystemHeader {
              background-color: #5a6268 !important;
              color: white !important;
              padding: 6px 10px;
              margin: 0 0 6px 0;
              font-size: 8pt;
              font-weight: bold;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            
            .printTable {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 8px;
              font-size: 6.5pt;
            }
            
            .printTable thead th {
              background-color: #3a57e8 !important;
              color: white !important;
              text-transform: uppercase;
              font-weight: 600;
              font-size: 6pt;
              padding: 5px 4px;
              border: 1px solid #3a57e8;
              text-align: left;
              white-space: nowrap;
            }
            
            .printTable tbody td {
              padding: 4px 3px;
              font-size: 6.5pt;
              border: 1px solid #dee2e6;
            }
            
            .printTable tbody tr {
              background-color: #ffffff;
            }
            
            .printTable tbody tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            
            .printAvailable {
              color: #28a745 !important;
              font-weight: bold;
            }
            
            .printShortage {
              color: #dc3545 !important;
              font-weight: bold;
            }
            
            .printOrder {
              background-color: #fff3cd !important;
              font-weight: bold;
            }
            
            .printSignatureSection {
              display: flex;
              justify-content: space-between;
              margin-top: 35px;
              margin-bottom: 15px;
              gap: 30px;
            }
            
            .printSignatureBox {
              flex: 1;
            }
            
            .printSignatureTitle {
              font-weight: bold;
              font-size: 9pt;
              margin-bottom: 10px;
            }
            
            .printSignatureLine {
              margin-bottom: 12px;
            }
            
            .printSignatureLine span {
              display: block;
              font-size: 8pt;
              margin-bottom: 4px;
            }
            
            .printLine {
              border-bottom: 1px solid #333;
              width: 55%;
              margin-top: 2px;
            }
            
            .printFooterNotes {
              margin-top: 10px;
              padding-top: 8px;
              border-top: 1px solid #ddd;
              font-size: 6.5pt;
              color: #666;
            }
            
            .printFooterNotes p {
              margin-bottom: 3px;
            }
          </style>
        </head>
        <body>
          <div class="printContent">
            ${printContent}
          </div>
          <div class="page-footer">
            <p>
              Coyote Cabling, Inc. • 123 Copper Way, Dallas, TX 75201 •
              procurement@coyotecabling.com
            </p>
          </div>
        </body>
      </html>
    `;

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = pdfContent;
    document.body.appendChild(tempDiv);

    const opt = {
      margin: [15, 12, 15, 12],
      filename: `${bomInfo.bomNumber || "BOM-Report"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "landscape",
      },
    };

    html2pdf()
      .set(opt)
      .from(tempDiv)
      .save()
      .then(() => {
        document.body.removeChild(tempDiv);
      });
  };

  const handlePrint = () => {
    if (!printContentRef.current) return;

    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";
    document.body.appendChild(printFrame);

    const printContent = printContentRef.current.innerHTML;
    const printDocument = printFrame.contentWindow.document;
    printDocument.open();
    printDocument.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${bomInfo.bomNumber || "BOM-Report"}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: Arial, sans-serif;
              font-size: 9pt;
              color: #000;
              background: #fff;
              margin: 0;
              padding: 0;
            }
            
            @page {
              size: A4 landscape;
              margin: 15mm 12mm 15mm 12mm;
            }
            
            @media print {
              body {
                print-color-adjust: exact !important;
                -webkit-print-color-adjust: exact !important;
              }
              
              .printSystemHeader,
              .printTable thead th,
              .printAvailable,
              .printShortage,
              .printOrder {
                print-color-adjust: exact !important;
                -webkit-print-color-adjust: exact !important;
              }
              
              .page-footer {
                position: fixed;
                bottom: 0mm;
                left: 12mm;
                right: 12mm;
                text-align: center;
                border-top: 2px solid #3a57e8;
                padding-top: 6px;
                font-size: 7pt;
                font-weight: 600;
                color: #333;
                background: white;
                z-index: 9999;
              }
              
              .printContent {
                padding-bottom: 20mm;
              }
            }
            
            .page-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 2px solid #0056b3;
              padding-bottom: 8px;
              margin-bottom: 10px;
            }
            
            .page-footer {
              position: fixed;
              bottom: 0mm;
              left: 12mm;
              right: 12mm;
              text-align: center;
              border-top: 2px solid #3a57e8;
              padding-top: 6px;
              font-size: 7pt;
              font-weight: 600;
              color: #333;
              background: white;
            }
            
            .printLogo {
              height: auto;
              width: 70px;
            }
            
            .printTitle {
              text-align: right;
            }
            
            .printMainTitle {
              margin: 0;
              font-size: 13pt;
              color: #3a57e8;
              font-weight: 600;
              letter-spacing: 0.5px;
            }
            
            .printBomNumber {
              margin: 3px 0 0 0;
              font-size: 7.5pt;
            }
            
            .printBold {
              font-weight: bold;
            }
            
            .printContent {
              padding-bottom: 20mm;
            }
            
            .printMetadata {
              border: 1px solid #ddd;
              padding: 8px 10px;
              margin-bottom: 10px;
              background-color: #f5f5f5;
              page-break-inside: avoid;
            }

            .printMetadataRow {
              display: flex;
              justify-content: space-between;
              margin-bottom: 4px;
            }

            .printMetadataRow:last-child {
              margin-bottom: 0;
            }

            .printMetadataItem {
              flex: 1;
              font-size: 8pt;
              display: flex;
            }

            .printMetadataLabel {
              font-weight: bold;
              min-width: 120px;
              display: inline-block;
            }
            
            .printHeading {
              margin: 10px 0 8px 0;
              font-size: 11pt;
              font-weight: 600;
              page-break-after: avoid;
            }
            
            .printSystem {
              margin-bottom: 12px;
            }
            
            .printSystemHeader {
              background-color: #5a6268;
              color: white;
              padding: 6px 10px;
              margin: 0 0 6px 0;
              font-size: 8pt;
              font-weight: bold;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              page-break-after: avoid;
            }
            
            .printTable {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 8px;
              font-size: 6.5pt;
              page-break-inside: auto;
            }
            
            .printTable thead th {
              background-color: #3a57e8;
              color: white;
              text-transform: uppercase;
              font-weight: 600;
              font-size: 6pt;
              padding: 5px 4px;
              border: 1px solid #3a57e8;
              text-align: left;
              white-space: nowrap;
            }
            
            .printTable tbody td {
              padding: 4px 3px;
              font-size: 6.5pt;
              border: 1px solid #dee2e6;
            }
            
            .printTable tbody tr {
              background-color: #ffffff;
              page-break-inside: avoid;
              page-break-after: auto;
            }
            
            .printTable tbody tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            
            .printAvailable {
              color: #28a745;
              font-weight: bold;
            }
            
            .printShortage {
              color: #dc3545;
              font-weight: bold;
            }
            
            .printOrder {
              background-color: #fff3cd;
              font-weight: bold;
            }
            
            .printSignatureSection {
              display: flex;
              justify-content: space-between;
              margin-top: 35px;
              margin-bottom: 15px;
              gap: 30px;
              page-break-inside: avoid;
            }
            
            .printSignatureBox {
              flex: 1;
            }
            
            .printSignatureTitle {
              font-weight: bold;
              font-size: 9pt;
              margin-bottom: 10px;
            }
            
            .printSignatureLine {
              margin-bottom: 12px;
            }
            
            .printSignatureLine span {
              display: block;
              font-size: 8pt;
              margin-bottom: 4px;
            }
            
            .printLine {
              border-bottom: 1px solid #333;
              width: 55%;
              margin-top: 2px;
            }
            
            .printFooterNotes {
              margin-top: 10px;
              padding-top: 8px;
              border-top: 1px solid #ddd;
              font-size: 6.5pt;
              color: #666;
              page-break-inside: avoid;
            }
            
            .printFooterNotes p {
              margin-bottom: 3px;
            }
          </style>
        </head>
        <body>
          <div class="printContent">
            ${printContent}
          </div>
          <div class="page-footer">
            <p>
              Coyote Cabling, Inc. • 123 Copper Way, Dallas, TX 75201 •
              procurement@coyotecabling.com
            </p>
          </div>
        </body>
      </html>
    `);
    printDocument.close();

    setTimeout(() => {
      try {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();

        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 1000);
      } catch (error) {
        document.body.removeChild(printFrame);
      }
    }, 500);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "available":
        return "statusAvailable";
      case "shortage":
        return "statusShortage";
      case "order":
        return "qtyOrder";
      default:
        return "";
    }
  };

  const getPrintStatusClass = (status) => {
    switch (status) {
      case "available":
        return "printAvailable";
      case "shortage":
        return "printShortage";
      case "order":
        return "printOrder";
      default:
        return "";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const renderSystemTable = (systemName, systemData) => (
    <div key={systemName} className="systemSection">
      <div className="systemHeader">System: {systemName.toUpperCase()}</div>
      <div className="tableWrapper">
        <table className="dataTable">
          <thead>
            <tr>
              <th>Phase</th>
              <th>Product (Model/SKU)</th>
              <th>Unit</th>
              <th>Make</th>
              <th>Qty Required</th>
              <th>Qty In Stock</th>
              <th>Qty Allocated*</th>
              <th>Net Available</th>
              <th>QTY TO ORDER</th>
              <th>Location</th>
            </tr>
          </thead>
          <tbody>
            {systemData.map((item, index) => (
              <tr key={index}>
                <td>{item.phase}</td>
                <td>{item.product}</td>
                <td>{item.unit}</td>
                <td>{item.manufacturer}</td>
                <td>{item.qtyRequired}</td>
                <td>{item.qtyInStock}</td>
                <td>{item.qtyAllocated}</td>
                <td className={getStatusClass(item.status)}>
                  {item.netAvailable}
                </td>
                <td className={item.qtyToOrder > 0 ? "qtyOrder" : ""}>
                  {item.qtyToOrder}
                </td>
                <td>
                  {item.location && typeof item.location === 'string'
                    ? item.location
                      .split(",")
                      .map((loc) => loc.trim())
                      .filter(Boolean)
                      .map((loc, index, arr) => (
                        <React.Fragment key={index}>
                          {loc}
                          {index !== arr.length - 1 ? "," : ""}
                          <br />
                        </React.Fragment>
                      ))
                    : item.location || ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPrintSystemTable = (systemName, systemData) => {
    return `<div class="printSystem">
      <div class="printSystemHeader">SYSTEM: ${systemName.toUpperCase()}</div>
      <table class="printTable">
        <thead>
          <tr>
            <th>Phase</th>
            <th>Product (Model/SKU)</th>
            <th>Unit</th>
            <th>Make</th>
            <th>Qty Required</th>
            <th>Qty In Stock</th>
            <th>Qty Allocated*</th>
            <th>Net Available</th>
            <th>Qty To Order</th>
            <th>Location</th>
          </tr>
        </thead>
        <tbody>
          ${systemData
        .map((item) => {
          const statusClass = getPrintStatusClass(item.status);
          const orderClass = item.qtyToOrder > 0 ? "printOrder" : "";
          return `<tr>
              <td>${item.phase || ""}</td>
              <td>${item.product || ""}</td>
              <td>${item.unit || ""}</td>
              <td>${item.manufacturer || ""}</td>
              <td>${item.qtyRequired || 0}</td>
              <td>${item.qtyInStock || 0}</td>
              <td>${item.qtyAllocated || 0}</td>
              <td class="${statusClass}">${item.netAvailable || 0}</td>
              <td class="${orderClass}">${item.qtyToOrder || 0}</td>
              <td>${item.location && typeof item.location === 'string'
              ? item.location
                .split(",")
                .map((loc) => loc.trim())
                .filter(Boolean)
                .map((loc, index, arr) => {
                  return `${loc}${index !== arr.length - 1 ? "," : ""}`;
                })
                .join("<br/>")
              : item.location || ""
            }</td>
            </tr>`;
        })
        .join("")}
        </tbody>
      </table>
    </div>`;
  };

  if (loading) {
    return (
      <div className="loadingCard">
        <FadeLoaderComponent />
      </div>
    );
  }

  return (
    <div className="bomBody" style={{ borderRadius: "8px" }}>
      <div className="bomContainer" ref={alertRef}>
        <div className="breadcrumbRow noPrint">
          <div className="breadcrumbNav text-primary card-title">
            <h4
              onClick={() => navigate("/dashboard/app/job-list")}
              className="breadcrumbLink"
            >
              Job
            </h4>
            <h4 className="breadcrumbSeparator mx-2 card-title">{">"}</h4>
            <h4
              onClick={() => navigate(`/dashboard/app/job-edit/${jobId}`)}
              className="breadcrumbLink text-primary card-title"
            >
              {jobName || "Loading..."}
            </h4>

            <h4 className="breadcrumbSeparator mx-2 card-title">{">"}</h4>
            <h4 className="breadcrumbActive card-title">
              {bomInfo.bomNumber || "Loading..."}
            </h4>
          </div>

          {(hasBomPrintPermission ||
            decodedToken?.roles?.some(
              (role) => role.role_type === roleTypes.SUPER_ADMIN
            )) && (
              <div
                className="printButtonContainer noPrint"
                style={{ display: "flex", gap: "10px" }}
              >
                <button
                  onClick={handleDownloadPDF}
                  disabled={totals?.totalProducts < 1}
                  style={{
                    opacity: totals?.totalProducts < 1 && "0.6",
                    cursor: totals?.totalProducts < 1 && "no-drop",
                    whiteSpace: "nowrap",
                  }}
                  className="printButton"
                >
                  Download PDF
                </button>
                <button
                  onClick={handlePrint}
                  disabled={totals?.totalProducts < 1}
                  style={{
                    opacity: totals?.totalProducts < 1 && "0.6",
                    cursor: totals?.totalProducts < 1 && "no-drop",
                    whiteSpace: "nowrap",
                  }}
                  className="printButton"
                >
                  Print BOM
                </button>
              </div>
            )}
        </div>

        <div ref={printContentRef} style={{ display: "none" }}>
          <div className="page-header">
            <img src={logo} alt="Coyote Logo" className="printLogo" />
            <div className="printTitle">
              <h1 className="printMainTitle">BILL OF MATERIALS (BOM)</h1>
              <p className="printBomNumber">
                BOM Number:{" "}
                <span className="printBold">{bomInfo.bomNumber || "N/A"}</span>
              </p>
            </div>
          </div>

          <div className="printMetadata">
            <div className="printMetadataRow">
              <div className="printMetadataItem">
                <span className="printMetadataLabel">Project/Job:</span>{" "}
                {bomInfo.jobName}
              </div>
              <div className="printMetadataItem">
                <span className="printMetadataLabel">Receiving Warehouse:</span>{" "}
                {bomInfo.warehouseName}
              </div>
            </div>
            <div className="printMetadataRow">
              <div className="printMetadataItem">
                <span className="printMetadataLabel">Prepared By:</span>{" "}
                {bomInfo.createdBy}
              </div>
              <div className="printMetadataItem">
                <span className="printMetadataLabel">Date Generated:</span>{" "}
                {formatDate(bomInfo.dateGenerated)}
              </div>
            </div>
          </div>

          {(() => {
            const productsToOrder = {};
            const inStockProducts = {};
            let hasProductsToOrder = false;
            let hasInStockProducts = false;

            Object.entries(bomData).forEach(([systemName, systemData]) => {
              const toOrder = systemData.filter((item) => item.qtyToOrder > 0);
              const inStock = systemData.filter(
                (item) => item.qtyToOrder === 0
              );

              if (toOrder.length > 0) {
                productsToOrder[systemName] = toOrder;
                hasProductsToOrder = true;
              }
              if (inStock.length > 0) {
                inStockProducts[systemName] = inStock;
                hasInStockProducts = true;
              }
            });

            if (!hasProductsToOrder) {
              return (
                <>
                  <h3 className="printHeading">Detailed Requirements</h3>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: Object.entries(bomData)
                        .map(([systemName, systemData]) =>
                          renderPrintSystemTable(systemName, systemData)
                        )
                        .join(""),
                    }}
                  />
                </>
              );
            }

            let html = "";

            if (hasProductsToOrder) {
              html += '<h3 class="printHeading">Detailed Summary</h3>';
              html += Object.entries(productsToOrder)
                .map(([systemName, systemData]) =>
                  renderPrintSystemTable(systemName, systemData)
                )
                .join("");
            }

            if (hasInStockProducts) {
              html +=
                '<h3 class="printHeading" style="margin-top: 15px;">In-Stock Products</h3>';
              html += Object.entries(inStockProducts)
                .map(([systemName, systemData]) =>
                  renderPrintSystemTable(systemName, systemData)
                )
                .join("");
            }

            return <div dangerouslySetInnerHTML={{ __html: html }} />;
          })()}

          <div className="printFooterNotes">
            <p>
              *The stock information in this report is accurate only at the
              moment of generation. Inventory levels may change afterward due to
              ongoing operations
            </p>
            <p>
              <strong>Qty Allocated</strong> are the number of units already
              reserved for other active jobs/BOMs (not including this job/BOM)
            </p>
          </div>

          <div className="printSignatureSection">
            <div className="printSignatureBox">
              <div className="printSignatureTitle">Prepared By:</div>
              <div className="printSignatureLine">
                <span>Name / Title</span>
                <div className="printLine"></div>
              </div>
              <div className="printSignatureLine">
                <span>Signature</span>
                <div className="printLine"></div>
              </div>
              <div className="printSignatureLine">
                <span>Date</span>
                <div className="printLine"></div>
              </div>
            </div>

            <div className="printSignatureBox">
              <div className="printSignatureTitle">Approved By:</div>
              <div className="printSignatureLine">
                <span>Name / Title</span>
                <div className="printLine"></div>
              </div>
              <div className="printSignatureLine">
                <span>Signature</span>
                <div className="printLine"></div>
              </div>
              <div className="printSignatureLine">
                <span>Date</span>
                <div className="printLine"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bomHeader">
          <div className="logoSection">
            <img src={logo} alt="Coyote Logo" className="logoImage" />
          </div>
          <div className="titleSection">
            <h1 className="mainTitle">BILL OF MATERIALS (BOM)</h1>
            <p className="bomNumber">
              BOM Number:{" "}
              <span className="boldText">{bomInfo.bomNumber || "N/A"}</span>
            </p>
          </div>
        </div>

        <div className="metadataBox">
          <div className="metadataRow">
            <div className="metadataItem">
              <span className="metadataLabel">Project/Job:</span>{" "}
              {bomInfo.jobName}
            </div>
            <div className="metadataItem">
              <span className="metadataLabel">Receiving Warehouse:</span>{" "}
              {bomInfo.warehouseName}
            </div>
          </div>
          <div className="metadataRow">
            <div className="metadataItem">
              <span className="metadataLabel">Prepared By:</span>{" "}
              {bomInfo.createdBy}
            </div>
            <div className="metadataItem">
              <span className="metadataLabel">Date Generated:</span>{" "}
              {formatDate(bomInfo.dateGenerated)}
            </div>
          </div>
        </div>

        <div className="summaryGrid">
          <div className="summaryCard">
            <p className="summaryLabel">Total Products</p>
            <h3 className="summaryValue summaryInfo">{totals.totalProducts}</h3>
          </div>
          <div className="summaryCard">
            <p className="summaryLabel">Total Units Required</p>
            <h3 className="summaryValue summaryInfo">
              {totals.totalRequired} {totals.totalRequired !== null && "Units"}
            </h3>
          </div>
          <div className="summaryCard">
            <p className="summaryLabel">Available Units in Stock</p>
            <h3 className="summaryValue summaryInfo">
              {totals.totalAvailable}{" "}
              {totals.totalAvailable !== null && "Units"}
            </h3>
          </div>
          <div className="summaryCard">
            <p className="summaryLabel">Total Units To Order</p>
            <h3 className="summaryValue summaryDanger">
              {totals.totalToOrder} {totals.totalToOrder !== null && "Units"}
            </h3>
          </div>
        </div>

        <h3 className="detailedHeading">Detailed Requirements</h3>

        {Object.keys(bomData).length === 0 ? (
          <div className="alertInfo">No products found in this BOM.</div>
        ) : (
          Object.entries(bomData).map(([systemName, systemData]) =>
            renderSystemTable(systemName, systemData)
          )
        )}

        <div className="footerNotes">
          <p className="footerText">
            *The stock information in this report is accurate only at the moment
            of generation. Inventory levels may change afterward due to ongoing
            operations
          </p>
          <p className="footerText">
            <strong>Qty Required</strong> are the total number of units needed
            to complete this job
          </p>
          <p className="footerText">
            <strong>Qty In Stock</strong> are the total number of units
            currently present in the warehouse, including both allocated
            (reserved for jobs) and unallocated (free) units (Qty Allocated +
            Net Available)
          </p>
          <p className="footerText">
            <strong>Qty Allocated</strong> are the number of units already
            reserved for other active jobs/BOMs (not including this job/BOM)
          </p>
          <p className="footerText">
            <strong>Net Available</strong> are the number of units in the
            warehouse that are not allocated to any job, fully free and
            available for use
          </p>
          <p className="footerText">
            <strong>Qty To Order</strong> are the number of units that must be
            purchased because the warehouse does not have enough free (Net
            Available) stock(Qty Required – Net Available)
          </p>
        </div>

        <div className="companyFooter">
          <p className="companyInfo">
            Coyote Cabling, Inc. • 123 Copper Way, Dallas, TX 75201 •
            procurement@coyotecabling.com
          </p>
        </div>
      </div>
    </div>
  );
};
export default JobBomView;
